<template>
  <section v-if="fields" :class="rootClass" :style="rootStyle" :id="rendering?.dataSource">
    <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
      <div class="c-text-and-video-section__inner">
        <div class="c-text-and-video-section__content">
          <JssRichText class="c-text-and-video-section__title" :field="fields?.title" tag="h2" />
          <RichText
            v-if="fields.description?.value"
            class="c-text-and-video-section__description"
            :field="fields.description"
          />
        </div>

        <div class="c-text-and-video-section__media">
          <AdaptiveMedia
            v-if="hasMedia"
            :field="mediaField"
            :ratio="1.78"
            :show-play-button="true"
            :autoplay="false"
            :loop="false"
            :controls="true"
            :has-controls="true"
            is-background
          />
        </div>
      </div>
    </ContentGrid>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RichText, Text as JssText } from '@sitecore-jss/sitecore-jss-vue';
import AdaptiveMedia from '@/elements/AdaptiveMedia.vue';
import type { TextAndVideoSectionProps } from '@/types/components/TextAndVideoSection';
import utils from '@/utils';

const props = defineProps<TextAndVideoSectionProps>();

const fields = computed(() => props.fields);
const rendering = computed(() => props.rendering);
const mediaField = computed(() => fields.value?.video);
const hasMedia = computed(() => {
  const mediaFields = mediaField.value?.fields;

  return !!(
    mediaFields?.desktopImage?.value?.src ||
    mediaFields?.mobileImage?.value?.src ||
    mediaFields?.desktopVideo?.value?.src ||
    mediaFields?.desktopVideo?.value?.href ||
    mediaFields?.mobileVideo?.value?.src ||
    mediaFields?.mobileVideo?.value?.href
  );
});

const rootClass = computed(() =>
  utils.overall.baseComClass(props.fields, 'c-text-and-video-section')
);
const rootStyle = computed(() => utils.overall?.baseComStyle?.(fields.value) || {});
</script>

<style lang="scss">
@import '../../styles/variables';
@import '../../styles/functions';
@import '../../styles/mixins';

.c-text-and-video-section {
  background: $white;
  padding: 40px 0;

  &__inner {
    display: grid;
    grid-template-columns: 1fr;
    gap: 32px;
    align-items: center;
  }

  &__content {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  &__title {
    margin-bottom: 24px;
  }

  &__description {
    color: $black;
    font-size: 14px;
    line-height: 1.45;

    p {
      margin: 0;

      & + p {
        margin-top: 22px;
      }
    }
  }

  &__media {
    position: relative;
    overflow: hidden;
    min-height: 220px;
    border-radius: 6px;
    background: $color-light-mercury;

    &__adaptive-media {
      display: block;
      width: 100%;

      .e-adaptive-media__image,
      .e-adaptive-media__video,
      .e-video-player,
      video {
        width: 100%;
        height: auto;
      }

      .e-video-player {
        aspect-ratio: var(--ratio);
      }

      img {
        display: block;
      }
    }
  }

  @include tablet {
    &__inner {
      grid-template-columns: minmax(320px, 0.92fr) minmax(420px, 1.08fr);
      gap: 60px;
    }

    &__media {
      min-height: 300px;

      .e-adaptive-media,
      .e-video-player,
      video {
        min-height: 300px;
      }
    }
  }

  @include tablet-landscape {
    &__inner {
      gap: 56px;
    }
  }
}
</style>
